import { InvoiceLogError } from '@app/interface/log-invoices-interfaces';

/**
 * Client-side counterpart of APIMekanicGO's audit sanitizer (task 73015). Events are sanitized
 * BEFORE they reach the durable outbox, so secrets never rest in browser storage; the backend
 * sanitizes again on receipt. Keep bounds in sync with
 * src/modules/log-invoices/utils/audit-sanitizer.ts.
 */
export const AUDIT_MAX_EVENT_BYTES = 64 * 1024;
export const AUDIT_MAX_STACK_BYTES = 16 * 1024;
const MAX_STRING_BYTES = 4 * 1024;
const MAX_DEPTH = 5;
const MAX_ARRAY_ITEMS = 50;
const MAX_KEYS = 60;
const REDACTED = '[REDACTED]';

const SENSITIVE_KEY = /authori[sz]ation|cookie|token|passw(or)?d|secret|api[-_]?key|credential|bearer|signature|certificate|private/i;
const DROPPED_KEYS = new Set(['headers', 'config', 'request', 'req', 'res', 'socket', 'agent']);

const _encoder = new TextEncoder();
const _byteLength = (value: string): number => _encoder.encode(value).length;

/** Truncates by UTF-8 bytes without splitting a code point. */
export function truncateBytes(value: string, maxBytes: number): { value: string; truncated: boolean } {
  if (_byteLength(value) <= maxBytes) {
    return { value, truncated: false };
  }

  let end = Math.min(value.length, maxBytes);
  while (end > 0 && _byteLength(value.slice(0, end)) > maxBytes) {
    end--;
  }
  if (end > 0 && /[\uD800-\uDBFF]/.test(value[end - 1])) {
    end--;
  }
  return { value: value.slice(0, end), truncated: true };
}

function _scrubString(value: string): string {
  return value
    .replace(/Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi, `Bearer ${REDACTED}`)
    .replace(
      /((?:access_|refresh_|id_)?token|password|secret|api[-_]?key|authorization)(["']?\s*[:=]\s*["']?)[^\s"'&,;}]+/gi,
      `$1$2${REDACTED}`
    );
}

function _sanitizeValue(value: unknown, depth: number, seen: WeakSet<object>, truncated: Set<string>, path: string): unknown {
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value === 'string') {
    const cut = truncateBytes(_scrubString(value), MAX_STRING_BYTES);
    if (cut.truncated) {
      truncated.add(path);
    }
    return cut.value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'bigint') {
    return value.toString();
  }
  if (typeof value === 'function' || typeof value === 'symbol') {
    return undefined;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value !== 'object') {
    return undefined;
  }
  if (seen.has(value)) {
    return '[Circular]';
  }
  if (depth >= MAX_DEPTH) {
    truncated.add(path);
    return '[MaxDepth]';
  }

  seen.add(value);
  let result: unknown;
  if (Array.isArray(value)) {
    if (value.length > MAX_ARRAY_ITEMS) {
      truncated.add(path);
    }
    result = value.slice(0, MAX_ARRAY_ITEMS).map((item, i) => _sanitizeValue(item, depth + 1, seen, truncated, `${path}[${i}]`));
  } else {
    const out: Record<string, unknown> = {};
    const source = value as Record<string, unknown>;
    let count = 0;
    for (const key of Object.keys(source)) {
      if (DROPPED_KEYS.has(key)) {
        continue;
      }
      if (count++ >= MAX_KEYS) {
        truncated.add(path);
        break;
      }
      out[key] = SENSITIVE_KEY.test(key) ? REDACTED : _sanitizeValue(source[key], depth + 1, seen, truncated, `${path}.${key}`);
    }
    result = out;
  }
  seen.delete(value);
  return result;
}

/** Sanitizes arbitrary structured context (resource, partial results...). */
export function sanitizeAuditContext<T>(value: T): T {
  return _sanitizeValue(value, 0, new WeakSet<object>(), new Set<string>(), 'context') as T;
}

/**
 * Normalizes any thrown value (Error, HttpErrorResponse, plain object, string) into the persisted
 * error shape. Never fabricates a stack, never copies request headers/config.
 */
export function normalizeAuditError(error: unknown, context?: Record<string, unknown>): InvoiceLogError {
  const truncated = new Set<string>();
  const seen = new WeakSet<object>();
  const source = (error && typeof error === 'object') ? error as Record<string, unknown> : null;
  const nested = source?.['error'];

  const asString = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
  const asNumber = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined);

  const nestedObject = nested && typeof nested === 'object' ? nested as Record<string, unknown> : null;
  const messageSource = asString(source?.['message']) || asString(nestedObject?.['message']) || asString(nested) || (typeof error === 'string' ? error : undefined) || 'Unknown error';

  const result: InvoiceLogError = {
    name: asString(source?.['name']),
    message: truncateBytes(_scrubString(messageSource), MAX_STRING_BYTES).value,
    status: asNumber(source?.['status']),
    statusText: asString(source?.['statusText']),
    code: (typeof source?.['code'] === 'string' || typeof source?.['code'] === 'number') ? source['code'] as string | number : undefined,
    url: (() => {
      const url = asString(source?.['url']);
      return url ? _scrubString(url.split('?')[0]) : undefined;
    })(),
  };

  const stack = asString(source?.['stack']);
  if (stack) {
    const cut = truncateBytes(_scrubString(stack), AUDIT_MAX_STACK_BYTES);
    result.stack = cut.value;
    if (cut.truncated) {
      truncated.add('error.stack');
    }
  }

  if (nested !== undefined && nested !== null) {
    result.data = _sanitizeValue(nested, 1, seen, truncated, 'error.data');
  }
  if (context) {
    result.context = _sanitizeValue(context, 1, seen, truncated, 'error.context') as Record<string, unknown>;
  }
  if (truncated.size) {
    result.truncated = Array.from(truncated);
  }

  // Drop undefined keys so the serialized event is minimal and stable.
  return Object.fromEntries(Object.entries(result).filter(([, v]) => v !== undefined)) as InvoiceLogError;
}

/** Applies the 64 KiB event bound, shedding the least diagnostic fields first. */
export function enforceAuditEventBound<T extends { error?: InvoiceLogError; billingContext?: { truncated?: string[]; results?: unknown } }>(event: T): T {
  const size = () => _byteLength(JSON.stringify(event));
  if (size() <= AUDIT_MAX_EVENT_BYTES) {
    return event;
  }

  const dropped: string[] = [];
  const steps: Array<() => void> = [
    () => { if (event.error?.data !== undefined) { delete event.error.data; dropped.push('error.data'); } },
    () => { if (event.error?.context !== undefined) { delete event.error.context; dropped.push('error.context'); } },
    () => { if (event.billingContext?.results !== undefined) { delete event.billingContext.results; dropped.push('billingContext.results'); } },
    () => { if (event.error?.stack) { event.error.stack = truncateBytes(event.error.stack, 2048).value; dropped.push('error.stack'); } },
    () => { if (event.error?.message) { event.error.message = truncateBytes(event.error.message, 1024).value; dropped.push('error.message'); } },
  ];

  for (const step of steps) {
    step();
    if (event.billingContext) {
      event.billingContext.truncated = Array.from(new Set([...(event.billingContext.truncated ?? []), ...dropped]));
    }
    if (size() <= AUDIT_MAX_EVENT_BYTES) {
      break;
    }
  }
  return event;
}
